import { useEffect, useMemo } from 'react'

import type { VuiComponent } from '../core'
import type { LinkProps } from './link.types'

import { styled, useStyleConfig, vui } from '../core'
import T from '../t'
import { cs, filterUndefined, isReactText, isString } from '../utils'
import { LinkProvider } from './context'
import LinkIcon from './linkIcon'
import LinkText from './linkText'

const linkSwipeAnimation = `
  @keyframes linkSwipe {
    from {
      background-size: 0 1px;
    }
    to {
      background-size: 100% 1px;
    }
  }
`

export const LinkBase = styled.aBox.attrs((props: LinkProps) => ({
  isUnderlined: props.isUnderlined || false,
}))`
  ${linkSwipeAnimation}
  display: inline;
  cursor: pointer;
  position: relative;
  line-height: 1.5;
  outline: none;
  width: fit-content;
  text-decoration: none;

  &:not(.vui-linkNoUnderline) > .vui-t {
    position: relative;
    background: linear-gradient(to right, currentcolor, currentcolor);
    background-size: ${({ isUnderlined }) => (isUnderlined ? '100% 1px' : '0 1px')};
    background-position: 0 100%;
    background-repeat: no-repeat;
    text-decoration: none;
    padding-bottom: 2px;
    line-height: 1.2;
  }

  &:hover > .vui-t {
    animation: ${({ isUnderlined }) =>
      isUnderlined ? 'linkSwipe 0.5s ease forwards' : 'linkSwipe 0.3s ease forwards'};
    background-size: 100% 1px;
  }
`

export const Link = vui<'a', LinkProps>((props, ref) => {
  const {
    children,
    className,
    fontWeight,
    startIcon,
    endIcon,
    iconLeft,
    iconRight,
    isExternal,
    isUnderlined,
    size,
    text,
    variant,
    weight,
    ...rest
  } = props

  const resolvedStartIcon = startIcon ?? iconLeft
  const resolvedEndIcon = endIcon ?? iconRight

  const styles = useStyleConfig('Link', props)
  const context = useMemo(() => filterUndefined({ size, variant }), [size, variant])
  const iconPadding = size === 'xs' ? '1.2rem' : '1.5rem'

  const aliasedProps = filterUndefined({
    fontWeight: weight || fontWeight,
    rel: isExternal ? 'noopener' : undefined,
    target: isExternal ? '_blank' : undefined,
    pl: resolvedStartIcon ? iconPadding : undefined,
    pr: resolvedEndIcon ? iconPadding : undefined,
  })

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (iconLeft !== undefined) {
        console.warn('[Link] `iconLeft` is deprecated. Use `startIcon` instead.')
      }
      if (iconRight !== undefined) {
        console.warn('[Link] `iconRight` is deprecated. Use `endIcon` instead.')
      }
    }
  }, [iconLeft, iconRight])

  return (
    <LinkProvider value={context} data-size={context.size} data-variant={context.variant}>
      <LinkBase
        className={cs('vui-link', className)}
        focusRing={2}
        isUnderlined={isUnderlined}
        ref={ref}
        {...styles.container}
        {...aliasedProps}
        {...rest}
      >
        {isString(resolvedStartIcon) ? <LinkIcon mr={1} left={0} name={resolvedStartIcon} /> : resolvedStartIcon}
        {children ? <T fontSize={'inherit'}>{children}</T> : isReactText(text) && <LinkText text={text} />}
        {isString(resolvedEndIcon) ? <LinkIcon ml={1} right={0} name={resolvedEndIcon} /> : resolvedEndIcon}
      </LinkBase>
    </LinkProvider>
  )
}) as VuiComponent<'a', LinkProps> & {
  Icon: typeof LinkIcon
  Text: typeof LinkText
}

Link.Icon = LinkIcon
Link.Text = LinkText
Link.displayName = 'Link'

export default Link
